/**
 * F38 (Wave 2 Track D) — admin "edit subscription tier pricing" modal.
 *
 * Composes Modal + FormField + CurrencyInput (monthly/annual price, USD minor
 * units) + NumberInput (commission %, persisted as basis points) + Textarea
 * (mandatory reason ≥10) + a forward-only Banner. Edits pricing + commission bp
 * only — feature gating is code-defined (D11) and shown read-only on the
 * catalog page, never editable here.
 *
 * If-Match optimistic concurrency: the dialog sends `tier.updatedAt` so a stale
 * edit returns 409 SUBSCRIPTION_STALE (surfaced inline). Gated server-side on
 * `subscription_tiers:write`; the catalog page hides the CTA without it.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  CurrencyInput,
  FormField,
  Modal,
  NumberInput,
  Textarea,
} from '@bravemove/ui-web';
import {
  EditTierPricingInputSchema,
  SUBSCRIPTION_MAX_RATE_BASIS_POINTS,
  SUBSCRIPTION_MIN_RATE_BASIS_POINTS,
} from '@bravemove/types';
import { adminSubscriptionApi, type AdminSubscriptionTier } from '../../api/subscription';

export interface EditTierPricingDialogProps {
  readonly open: boolean;
  readonly tier: AdminSubscriptionTier;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

const MIN_PERCENT = SUBSCRIPTION_MIN_RATE_BASIS_POINTS / 100; // 0
const MAX_PERCENT = SUBSCRIPTION_MAX_RATE_BASIS_POINTS / 100; // 50

function bpToPercent(bp: number): number {
  return Math.round((bp / 100) * 100) / 100;
}

function percentToBp(pct: number): number {
  return Math.round(pct * 100);
}

export function EditTierPricingDialog({
  open,
  tier,
  onConfirm,
  onClose,
}: EditTierPricingDialogProps) {
  const { t } = useI18n();
  const [monthly, setMonthly] = useState<number>(tier.monthlyPriceAmount);
  const [annual, setAnnual] = useState<number>(tier.annualPriceAmount);
  const [commissionPercent, setCommissionPercent] = useState<number>(
    bpToPercent(tier.commissionBasisPoints),
  );
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset controlled state on (re-)open so a second open starts from the
    // tier's current values (mirrors the F23 EditRateDialog reset-on-open).
    /* eslint-disable react-hooks/set-state-in-effect */
    setMonthly(tier.monthlyPriceAmount);
    setAnnual(tier.annualPriceAmount);
    setCommissionPercent(bpToPercent(tier.commissionBasisPoints));
    setReason('');
    setServerError(null);
    setSubmitting(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open, tier.monthlyPriceAmount, tier.annualPriceAmount, tier.commissionBasisPoints]);

  const commissionBp = percentToBp(Number(commissionPercent ?? 0));
  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= 10 && reasonTrimmed.length <= 500;
  const rateValid =
    commissionBp >= SUBSCRIPTION_MIN_RATE_BASIS_POINTS &&
    commissionBp <= SUBSCRIPTION_MAX_RATE_BASIS_POINTS;
  const disableSubmit = submitting || !reasonValid || !rateValid;

  const submit = async () => {
    setServerError(null);
    const candidate = {
      monthlyPriceAmount: monthly,
      annualPriceAmount: annual,
      commissionBasisPoints: commissionBp,
      reason: reasonTrimmed,
    };
    const parsed = EditTierPricingInputSchema.safeParse(candidate);
    if (!parsed.success) {
      setServerError(
        t('admin:subscription.dialog.edit_pricing.error_invalid', {
          defaultValue: 'Check the prices, commission rate (0–50%), and reason (10–500 chars).',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await adminSubscriptionApi.editTierPricing(tier.code, parsed.data, tier.updatedAt);
      onConfirm();
      onClose();
    } catch (err) {
      const code =
        err !== null && typeof err === 'object' && 'code' in err
          ? (err as { code?: string }).code
          : null;
      if (code === 'SUBSCRIPTION_STALE') {
        setServerError(
          t('admin:subscription.dialog.edit_pricing.error_stale', {
            defaultValue: 'This tier changed since you opened the editor. Reload and try again.',
          }),
        );
      } else {
        setServerError(
          t('admin:subscription.toast.error_generic', {
            defaultValue: 'Something went wrong. Please try again.',
          }),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:subscription.dialog.edit_pricing.title', {
        tier: tier.displayName,
        defaultValue: 'Edit pricing — {tier}',
      })}
      description={t('admin:subscription.dialog.edit_pricing.description', {
        defaultValue:
          'Pricing + commission apply forward-only. Features are fixed per tier and not editable here.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="edit-tier-pricing-cancel"
          >
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="edit-tier-pricing-confirm"
          >
            {t('common:save', { defaultValue: 'Save' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="edit-tier-pricing-dialog"
        data-tier-code={tier.code}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div data-testid="edit-tier-pricing-forward-only-banner">
          <Banner
            tone="info"
            title={t('admin:subscription.dialog.edit_pricing.banner_title', {
              defaultValue: 'Forward-only change',
            })}
          >
            {t('admin:subscription.dialog.edit_pricing.banner_body', {
              defaultValue:
                'Existing invoices keep their billed amount. New invoices use the new pricing.',
            })}
          </Banner>
        </div>

        <FormField
          label={t('admin:subscription.dialog.edit_pricing.monthly_label', {
            defaultValue: 'Monthly price',
          })}
        >
          <CurrencyInput
            value={monthly}
            currency="USD"
            ariaLabel={t('admin:subscription.dialog.edit_pricing.monthly_label', {
              defaultValue: 'Monthly price',
            })}
            onChange={(v) => setMonthly(v)}
          />
        </FormField>

        <FormField
          label={t('admin:subscription.dialog.edit_pricing.annual_label', {
            defaultValue: 'Annual price',
          })}
        >
          <CurrencyInput
            value={annual}
            currency="USD"
            ariaLabel={t('admin:subscription.dialog.edit_pricing.annual_label', {
              defaultValue: 'Annual price',
            })}
            onChange={(v) => setAnnual(v)}
          />
        </FormField>

        <FormField
          label={t('admin:subscription.dialog.edit_pricing.commission_label', {
            defaultValue: 'Commission rate (%)',
          })}
          helper={t('admin:subscription.dialog.edit_pricing.commission_helper', {
            defaultValue: 'Between 0% and 50%. Applied as a tier-derived rate on tier change.',
          })}
        >
          <NumberInput
            value={commissionPercent}
            min={MIN_PERCENT}
            max={MAX_PERCENT}
            step={0.01}
            data-testid="edit-tier-pricing-commission-input"
            onChange={(v) => setCommissionPercent(Number(v))}
          />
        </FormField>

        <FormField
          label={t('admin:subscription.dialog.reason_label', {
            defaultValue: 'Reason (audit-logged)',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            data-testid="edit-tier-pricing-reason"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        {serverError ? (
          <div data-testid="edit-tier-pricing-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default EditTierPricingDialog;
